import React, {FC} from "react";
import {App, Button, Col, Form, Input, Modal, Row, Space} from "antd";
import {useTranslation} from "react-i18next";
import {EventHotelReturnDto, useCreateEventHotelMutation} from "../../../GraphQL/generated/graphql";
import {getUserFacingErrorMessage, UserFacingErrorLabels} from "../../../userFacingErrorUtils";
import {
    buildHotelCreateInput,
    findMatchingEventHotel,
    firstMutationItem,
    HotelCatalogFormValues
} from "./eventConfigPayloads";

type HotelCatalogModalProps = {
    open: boolean;
    eventId?: number;
    errorLabels: UserFacingErrorLabels;
    // Used when the create mutation saves but returns no row.
    refetchEventHotels: () => Promise<Array<EventHotelReturnDto | null | undefined>>;
    onClose: () => void;
    onSaved: (hotel: EventHotelReturnDto) => void;
};

const emptyHotel: HotelCatalogFormValues = {name: "", address: "", city: "", country: ""};

export const HotelCatalogModal: FC<HotelCatalogModalProps> = ({open, eventId, errorLabels, refetchEventHotels, onClose, onSaved}) => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const [form] = Form.useForm<HotelCatalogFormValues>();
    const [createHotel, {loading: creatingHotel}] = useCreateEventHotelMutation();

    const handleSubmit = async () => {
        if (!eventId) {
            return;
        }

        let values: HotelCatalogFormValues;
        try {
            values = {...emptyHotel, ...await form.validateFields()};
        } catch {
            return;
        }

        const hotelName = values.name.trim();
        try {
            const result = await createHotel({
                variables: {
                    input: buildHotelCreateInput({...values, name: hotelName}, eventId)
                }
            });
            const createdHotel = firstMutationItem(result.data?.createEventHotel)
                ?? findMatchingEventHotel(await refetchEventHotels(), hotelName, values);
            if (!createdHotel) {
                throw new Error(t("event-config-hotel-create-empty-response", {
                    defaultValue: "Hotel was not returned after saving."
                }));
            }

            onSaved(createdHotel);
            message.success(t("event-config-hotel-saved", {defaultValue: "Hotel saved"}));
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    return (
        <Modal
            title={t("event-config-add-hotel-to-catalog", {defaultValue: "Add hotel to catalog"})}
            open={open}
            onCancel={onClose}
            footer={null}
            destroyOnHidden
        >
            <Form form={form} layout="vertical" initialValues={emptyHotel} preserve={false}>
                <Form.Item
                    name="name"
                    label={t("hotel-name", {defaultValue: "Hotel name"})}
                    rules={[{
                        required: true,
                        whitespace: true,
                        message: t("event-config-hotel-name-required", {defaultValue: "Hotel name is required."})
                    }]}
                >
                    <Input placeholder={t("hotel-name", {defaultValue: "Hotel name"})}/>
                </Form.Item>
                <Form.Item name="address" label={t("address", {defaultValue: "Address"})}>
                    <Input placeholder={t("address", {defaultValue: "Address"})}/>
                </Form.Item>
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item name="city" label={t("city", {defaultValue: "City"})}>
                            <Input placeholder={t("city", {defaultValue: "City"})}/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item name="country" label={t("country", {defaultValue: "Country"})}>
                            <Input placeholder={t("country", {defaultValue: "Country"})}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Space wrap>
                    <Button type="primary" onClick={handleSubmit} loading={creatingHotel}>
                        {t("event-config-save-hotel", {defaultValue: "Save hotel"})}
                    </Button>
                    <Button onClick={onClose}>
                        {t("cancel", {defaultValue: "Cancel"})}
                    </Button>
                </Space>
            </Form>
        </Modal>
    );
};
